/**
 * The drill engine (design §2): composes the engine composables and returns
 * the object `useStep` has always returned (same 37 keys, same refs).
 * The one late binding: the filter loader's `onLoaded` runs into the progress
 * recorder through a closure created before the recorder exists (it is only
 * called after an awaited fetch, so the recorder is always there by then).
 */
import { computed } from 'vue';
import type { DrillSettings } from 'src/types/course/drill';
import { useStepData } from '../useStepData';
import { useDrillConfig } from './useDrillConfig';
import { useUserStepRecord } from './useUserStepRecord';
import { useFilterStackSource } from './useFilterStackSource';
import { useStepContent } from './useStepContent';
import { useStepProgress } from './useStepProgress';
import { useProgressPersistence } from './useProgressPersistence';
import {
  useProgressRecorder,
  type UseProgressRecorderReturn,
} from './useProgressRecorder';
import { useDrillAnswers } from './useDrillAnswers';
import type { StackItems } from '../utilities/stackItems';

export type { ProgressData } from './useProgressRecorder';

export const useStepEngine = (
  documentId: string,
  userStepId: number,
  userSectionDocId?: string,
) => {
  if (!documentId || !userStepId)
    throw new Error('useStep: requires document or userStepId');
  const { isLoading, loadError, validationErrors, documentStepRepo } =
    useStepData(documentId);

  const config = useDrillConfig({ documentStepRepo });
  const record = useUserStepRecord({ userStepId });
  const { repoUserStep, currentParsedProgress, currentStepResults } = record;

  let recorder: UseProgressRecorderReturn | null = null;
  const filter = useFilterStackSource({
    documentId,
    userSectionDocId,
    stepType: config.stepType,
    stepDrill: config.stepDrill,
    filterSettings: config.filterSettings,
    documentStepRepo,
    repoUserStep,
    onLoaded: (items: StackItems) => recorder?.recordSkippedFilter(items),
  });

  const { stackSource, orderedStackSource, isStackReady } = useStepContent({
    stepType0: config.stepType0,
    stepDrill: config.stepDrill,
    isFilterStackSource: filter.isFilterStackSource,
    filterStackSource: filter.filterStackSource,
    isFilterStackSourceFetched: filter.isFilterStackSourceFetched,
    userSectionDocId,
  });

  const progress = useStepProgress({
    orderedStackSource,
    currentStepResults,
    currentParsedProgress,
    repoUserStep,
    isAnswered: record.isAnswered,
  });
  const { currentStackItem, isCompletedStep } = progress;

  const { updateStrapiProgress, awaitSaves, resetStepData } =
    useProgressPersistence({
      repoUserStep,
      score: progress.score,
      isCompletedStep,
      timeAccumulator: progress.timeAccumulator,
    });

  recorder = useProgressRecorder({
    repoUserStep,
    currentParsedProgress,
    documentStepRepo,
    stepDrill: config.stepDrill,
    stepType0: config.stepType0,
    orderedStackSource,
    currentStackItem,
    updateStrapiProgress,
  });

  const answers = useDrillAnswers({
    strategy: config.strategy,
    drillContext: config.drillContext,
    currentStackItem,
  });

  const stepSolveProps = computed(() => ({
    orderedStackSource: orderedStackSource.value,
    currentStackItem: currentStackItem.value,
    drillType: config.drillType.value,
  }));

  const stepDrillProps = computed(() => ({
    currentStackItem: currentStackItem.value,
    drillSettings: config.drillSettings.value as DrillSettings,
    drillQaSettings: config.drillQaSettings.value,
    drillQaOptions: config.drillQaOptions.value,
    drillType: config.drillType.value,
    questionDisplayMethod: config.questionDisplayMethod.value,
    isQuestionHearingEnabled: config.isQuestionHearingEnabled.value,
    isQuestionReadingEnabled: config.isQuestionReadingEnabled.value,
  }));

  return {
    isLoading,
    loadError,
    validationErrors,
    documentStepRepo,
    isQuestionAnswered: progress.isQuestionAnswered,
    drillSettings: config.drillSettings,
    stepProgressPercent: progress.stepProgressPercent,
    errorCount: progress.errorCount,
    stepProgressLabel: progress.stepProgressLabel,
    answerText: answers.answerText,
    correctCount: progress.correctCount,
    timeAccumulator: progress.timeAccumulator,
    score: progress.score,
    stepName: config.stepName,
    filterStepType: config.filterStepType,
    resetStepData,
    awaitSaves,
    isSkippedStep: record.isSkippedStep,
    isStackReady,
    stepSolveProps,
    stepDrillProps,
    stepDrill: config.stepDrill,
    currentStackItem,
    orderedStackSource,
    stepTypeCamelCase: config.stepTypeCamelCase,
    checkAnswer: answers.checkAnswer,
    updateProgress: recorder.updateProgress,
    getFeedbackSubtitle: answers.getFeedbackSubtitle,
    getFeedbackAnswer: answers.getFeedbackAnswer,
    getCorrectFeedbackAnswer: answers.getCorrectFeedbackAnswer,
    repoUserStep,
    isCompletedStep,
    currentStepResults,
    stackSource,
    stepType: config.stepType,
    currentCorrectAnswer: answers.currentCorrectAnswer,
  };
};

export type UseStepEngineReturn = ReturnType<typeof useStepEngine>;
